import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "motion/react";

import { usePrefersReducedMotion } from "../../../hooks/usePrefersReducedMotion";

const LEFT_EYE_BASE = { cx: 76.5, cy: 88 };
const RIGHT_EYE_BASE = { cx: 102.5, cy: 88 };
const BLOB_BASE = { cx: 89.5, cy: 95, rx: 58, ry: 52 };
const EYE_RX = 5.51318;
const EYE_RY = 6.95782;

/** Angry click: eyes squint (cy+2, ry≈2.5). Brows sit just above the white dots. */
const ANGRY_EYE_CY = LEFT_EYE_BASE.cy + 2;
const ANGRY_EYE_RY = 2.5;
const ANGRY_EYE_TOP_Y = ANGRY_EYE_CY - ANGRY_EYE_RY;

const ANGRY_LEFT_BROW = {
  x1: LEFT_EYE_BASE.cx - EYE_RX - 2,
  y1: ANGRY_EYE_TOP_Y - 5,
  x2: LEFT_EYE_BASE.cx + EYE_RX - 1,
  y2: ANGRY_EYE_TOP_Y - 1,
};
const ANGRY_RIGHT_BROW = {
  x1: RIGHT_EYE_BASE.cx - EYE_RX + 1,
  y1: ANGRY_EYE_TOP_Y - 1,
  x2: RIGHT_EYE_BASE.cx + EYE_RX + 2,
  y2: ANGRY_EYE_TOP_Y - 5,
};

/**
 * Animated loading blob (Motion + SVG).
 *
 * @param {object} props
 * @param {string} [props.color="#00567a"] Blob fill colour
 * @param {string} [props.textColor="#00567a"] "Loading..." label colour
 * @param {number} [props.size=200] Blob width/height in px
 * @param {boolean} [props.showLoadingText=true] Show animated "Loading..." label
 */
const BlobLoader = ({
  color = "#00567a",
  textColor = "#00567a",
  size = 200,
  showLoadingText = true,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const [isAngry, setIsAngry] = useState(false);
  const blobControls = useAnimation();
  const leftEyeControls = useAnimation();
  const rightEyeControls = useAnimation();
  const angryTimeoutRef = useRef(null);
  const dartTimeoutRef = useRef(null);
  const sighTimeoutRef = useRef(null);

  const labelOffset = showLoadingText ? 60 : 0;

  const runIdleBlob = useCallback(async () => {
    if (reducedMotion || isAngry) return;
    await blobControls.start({
      ...BLOB_BASE,
      cy: [93, 97, 93, 95],
      rx: [58, 54, 58, 58],
      ry: [50, 55, 50, 52],
      transition: {
        duration: 2.5,
        repeat: Infinity,
        ease: "easeInOut",
      },
    });
  }, [blobControls, isAngry, reducedMotion]);

  const dartEyes = useCallback(async () => {
    if (reducedMotion || isAngry) return;

    const dx = (Math.random() - 0.5) * 10;
    const dy = (Math.random() - 0.5) * 8;
    const blink = Math.random() < 0.12;
    const eyeRy = blink ? 0.6 : EYE_RY;

    await Promise.all([
      leftEyeControls.start({
        cx: LEFT_EYE_BASE.cx + dx,
        cy: LEFT_EYE_BASE.cy + dy,
        rx: EYE_RX,
        ry: eyeRy,
        transition: { duration: 0.08, ease: "easeOut" },
      }),
      rightEyeControls.start({
        cx: RIGHT_EYE_BASE.cx + dx,
        cy: RIGHT_EYE_BASE.cy + dy,
        rx: EYE_RX,
        ry: eyeRy,
        transition: { duration: 0.08, ease: "easeOut" },
      }),
    ]);

    if (!blink) {
      await Promise.all([
        leftEyeControls.start({
          ...LEFT_EYE_BASE,
          rx: EYE_RX,
          ry: EYE_RY,
          transition: { duration: 0.06 },
        }),
        rightEyeControls.start({
          ...RIGHT_EYE_BASE,
          rx: EYE_RX,
          ry: EYE_RY,
          transition: { duration: 0.06 },
        }),
      ]);
    }
  }, [isAngry, leftEyeControls, reducedMotion, rightEyeControls]);

  const scheduleDart = useCallback(() => {
    if (reducedMotion) return;
    const delay = 80 + Math.random() * 400;
    dartTimeoutRef.current = window.setTimeout(async () => {
      await dartEyes();
      scheduleDart();
    }, delay);
  }, [dartEyes, reducedMotion]);

  const runSigh = useCallback(async () => {
    if (reducedMotion || isAngry) return;

    await Promise.all([
      leftEyeControls.start({
        cx: LEFT_EYE_BASE.cx,
        cy: LEFT_EYE_BASE.cy + 5,
        rx: EYE_RX,
        ry: 3,
        transition: { duration: 0.35, ease: "easeInOut" },
      }),
      rightEyeControls.start({
        cx: RIGHT_EYE_BASE.cx,
        cy: RIGHT_EYE_BASE.cy + 5,
        rx: EYE_RX,
        ry: 3,
        transition: { duration: 0.35, ease: "easeInOut" },
      }),
      blobControls.start({
        ...BLOB_BASE,
        cy: [95, 90, 98, 95],
        rx: [58, 62, 54, 58],
        ry: [52, 48, 56, 52],
        transition: { duration: 1.8, ease: "easeInOut" },
      }),
    ]);

    await Promise.all([
      leftEyeControls.start({
        ...LEFT_EYE_BASE,
        rx: EYE_RX,
        ry: EYE_RY,
        transition: { duration: 0.4 },
      }),
      rightEyeControls.start({
        ...RIGHT_EYE_BASE,
        rx: EYE_RX,
        ry: EYE_RY,
        transition: { duration: 0.4 },
      }),
    ]);

    runIdleBlob();
  }, [
    blobControls,
    isAngry,
    leftEyeControls,
    reducedMotion,
    rightEyeControls,
    runIdleBlob,
  ]);

  const scheduleSigh = useCallback(
    (firstDelayMs) => {
      if (reducedMotion) return;
      sighTimeoutRef.current = window.setTimeout(async () => {
        await runSigh();
        scheduleSigh(10000);
      }, firstDelayMs);
    },
    [reducedMotion, runSigh]
  );

  const handleClick = useCallback(() => {
    if (reducedMotion) return;

    setIsAngry(true);
    if (angryTimeoutRef.current) {
      window.clearTimeout(angryTimeoutRef.current);
    }
    angryTimeoutRef.current = window.setTimeout(() => {
      setIsAngry(false);
      runIdleBlob();
    }, 2000);

    Promise.all([
      leftEyeControls.start({
        cx: LEFT_EYE_BASE.cx,
        cy: LEFT_EYE_BASE.cy + 2,
        rx: EYE_RX,
        ry: 2.5,
        transition: { duration: 0.15 },
      }),
      rightEyeControls.start({
        cx: RIGHT_EYE_BASE.cx,
        cy: RIGHT_EYE_BASE.cy + 2,
        rx: EYE_RX,
        ry: 2.5,
        transition: { duration: 0.15 },
      }),
    ]);
  }, [leftEyeControls, reducedMotion, rightEyeControls, runIdleBlob]);

  useEffect(() => {
    if (reducedMotion) {
      blobControls.set(BLOB_BASE);
      leftEyeControls.set({ ...LEFT_EYE_BASE, rx: EYE_RX, ry: EYE_RY });
      rightEyeControls.set({ ...RIGHT_EYE_BASE, rx: EYE_RX, ry: EYE_RY });
      return undefined;
    }

    runIdleBlob();
    leftEyeControls.set({ ...LEFT_EYE_BASE, rx: EYE_RX, ry: EYE_RY });
    rightEyeControls.set({ ...RIGHT_EYE_BASE, rx: EYE_RX, ry: EYE_RY });
    scheduleDart();
    scheduleSigh(7000);

    return () => {
      if (dartTimeoutRef.current) window.clearTimeout(dartTimeoutRef.current);
      if (sighTimeoutRef.current) window.clearTimeout(sighTimeoutRef.current);
      if (angryTimeoutRef.current) window.clearTimeout(angryTimeoutRef.current);
    };
  }, [
    blobControls,
    leftEyeControls,
    reducedMotion,
    rightEyeControls,
    runIdleBlob,
    scheduleDart,
    scheduleSigh,
  ]);

  useEffect(() => {
    if (!isAngry && !reducedMotion) {
      runIdleBlob();
    }
  }, [isAngry, reducedMotion, runIdleBlob]);

  return (
    <div
      data-testid="blob-loader"
      style={{
        width: size,
        height: size + labelOffset,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: reducedMotion ? "default" : "pointer",
      }}
      onClick={handleClick}
      role="img"
      aria-label={showLoadingText ? "Loading" : "Loading animation"}
    >
      <svg
        style={{ width: size, height: size }}
        fill="none"
        preserveAspectRatio="xMidYMid meet"
        viewBox="0 0 179 179"
        aria-hidden
      >
        <g>
          <motion.ellipse
            cx="89.5"
            cy="147"
            rx="52"
            ry="6"
            fill="black"
            animate={
              reducedMotion
                ? { opacity: 0.2 }
                : { opacity: [0.18, 0.22, 0.18], rx: [52, 54, 52], ry: [6, 6.5, 6] }
            }
            transition={
              reducedMotion
                ? undefined
                : { duration: 2.5, repeat: Infinity, ease: "easeInOut" }
            }
          />

          <motion.g
            animate={
              reducedMotion
                ? undefined
                : isAngry
                  ? { rotate: [-15, 15, -15, 15, -10, 10, -5, 5, 0] }
                  : { rotate: [-3, 3, -2, 2, -3, 1, -3] }
            }
            transition={
              reducedMotion
                ? undefined
                : isAngry
                  ? { duration: 1.5, ease: "easeInOut" }
                  : { duration: 1.4, repeat: Infinity, ease: "easeInOut" }
            }
            style={{ transformOrigin: "89.5px 95px" }}
          >
            <motion.ellipse fill={color} animate={blobControls} initial={BLOB_BASE} />
          </motion.g>

          {isAngry && !reducedMotion && (
            <>
              <motion.line
                x1={ANGRY_LEFT_BROW.x1}
                y1={ANGRY_LEFT_BROW.y1}
                x2={ANGRY_LEFT_BROW.x2}
                y2={ANGRY_LEFT_BROW.y2}
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              />
              <motion.line
                x1={ANGRY_RIGHT_BROW.x1}
                y1={ANGRY_RIGHT_BROW.y1}
                x2={ANGRY_RIGHT_BROW.x2}
                y2={ANGRY_RIGHT_BROW.y2}
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              />
            </>
          )}

          <motion.ellipse
            fill="white"
            rx={EYE_RX}
            ry={EYE_RY}
            animate={leftEyeControls}
            initial={{ ...LEFT_EYE_BASE, rx: EYE_RX, ry: EYE_RY }}
          />
          <motion.ellipse
            fill="white"
            rx={EYE_RX}
            ry={EYE_RY}
            animate={rightEyeControls}
            initial={{ ...RIGHT_EYE_BASE, rx: EYE_RX, ry: EYE_RY }}
          />
        </g>
      </svg>

      {showLoadingText && (
        <p
          data-testid="blob-loader-label"
          style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            color: textColor,
            fontSize: 24,
            fontWeight: 500,
            letterSpacing: "0.18em",
            marginTop: 8,
            textAlign: "center",
            width: "100%",
          }}
        >
          Loading
          {[0, 0.3, 0.6].map((delay, i) => (
            <motion.span
              key={i}
              animate={reducedMotion ? undefined : { opacity: [0, 1, 0] }}
              transition={
                reducedMotion
                  ? undefined
                  : { duration: 1.2, repeat: Infinity, delay, ease: "easeInOut" }
              }
            >
              .
            </motion.span>
          ))}
        </p>
      )}
    </div>
  );
};

export default BlobLoader;
